import { useState } from 'react'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { useForensicsTools, useAddForensicsTool, useRemoveForensicsTool } from '../hooks'
import type { ForensicsTool, ForensicsToolType } from '../api'
import { Wrench, Trash2, Plus, Loader2 } from 'lucide-react'

const TOOL_TYPES: { value: ForensicsToolType; label: string }[] = [
    { value: 'yara', label: 'YARA rule' },
    { value: 'sigma', label: 'Sigma rule' },
    { value: 'script', label: 'Script' },
    { value: 'package', label: 'Package (apt/dnf/pip)' },
    { value: 'binary', label: 'Binary (upload)' },
]

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    vaultId: number | null
}

export function ForensicsToolsDialog({ open, onOpenChange, vaultId }: Props) {
    const { data, isLoading } = useForensicsTools(open ? vaultId : null)
    const add = useAddForensicsTool()
    const remove = useRemoveForensicsTool()
    const tools: ForensicsTool[] = Array.isArray(data) ? data : []

    const [name, setName] = useState('')
    const [toolType, setToolType] = useState<ForensicsToolType>('yara')
    const [content, setContent] = useState('')

    const submit = async () => {
        if (!name.trim()) return
        await add.mutateAsync({
            vaultId: vaultId ?? undefined,
            name: name.trim(),
            toolType,
            content: content.trim() || undefined,
        })
        setName(''); setContent('')
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Wrench className="h-5 w-5" /> Forensics tools</DialogTitle>
                    <DialogDescription>
                        Tools are installed at forensics session start from a manifest — the AppStream image is not edited here.
                        Config-only tools (rules, scripts, package names) are stored below; large binaries upload separately.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {isLoading ? (
                            <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
                        ) : tools.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No tools added yet.</p>
                        ) : tools.map((t) => (
                            <div key={t.id} className="flex items-center justify-between rounded border p-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    <Badge variant="secondary">{t.toolType}</Badge>
                                    <span className="text-sm truncate">{t.name}</span>
                                </div>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => remove.mutate(t.id)} title="Remove" disabled={remove.isPending}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-lg border p-3 space-y-2">
                        <Label className="text-sm font-medium">Add a tool</Label>
                        <div className="flex gap-2">
                            <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
                            <Select value={toolType} onValueChange={(v) => setToolType(v as ForensicsToolType)}>
                                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TOOL_TYPES.map((tt) => <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {toolType !== 'binary' ? (
                            <textarea
                                className="w-full rounded border bg-transparent p-2 text-sm font-mono min-h-[80px]"
                                placeholder={toolType === 'package' ? 'package names (one per line)' : 'rule / script content'}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                            />
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Binary upload uses a presigned URL requested over SQS — not yet wired (manifest contract pending).
                            </p>
                        )}
                        <Button size="sm" onClick={submit} disabled={!name.trim() || add.isPending || toolType === 'binary'}>
                            <Plus className="h-4 w-4 mr-1" /> {add.isPending ? 'Adding…' : 'Add tool'}
                        </Button>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
